import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';

import { generateId } from '@lc/util';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatIcon } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BspkMenu, MAX_ITEM_COUNT, MIN_ITEM_COUNT } from '../menu';
import { BspkListItem } from '../list-item';

export type DropdownOption<TValue = string> = {
  label: string;
  value: TValue;
};

export type DropdownSize = 'small' | 'medium' | 'large';

export const toDropdownOption = (label: string): DropdownOption => ({ label, value: label });

@Component({
  selector: 'bspk-dropdown',
  imports: [BspkMenu, CdkConnectedOverlay, CdkOverlayOrigin, MatIcon, BspkListItem],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class BspkDropdown<TValue = string> implements OnInit {
  /* Inputs */
  readonly value = model<TValue[]>([]);
  readonly ariaLabel = input.required<string>({ alias: 'aria-label' });
  readonly activeIndex = input<number>();
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly isMulti = input(false, { transform: booleanAttribute });
  readonly options = input<DropdownOption<TValue>[]>([]);
  readonly id = input(generateId('dropdown'));
  readonly noResultsMessage = input<string>('');
  readonly placeholder = input<string>('Select...');
  readonly invalid = input(false, { transform: booleanAttribute });
  readonly readOnly = input(false, { transform: booleanAttribute });
  readonly itemCountInput = input<number>(5, { alias: 'itemCount' });
  readonly itemCount = computed(() => Math.max(Math.min(this.itemCountInput(), MAX_ITEM_COUNT), MIN_ITEM_COUNT));
  readonly size = input<DropdownSize>('medium');
  readonly blur = output<FocusEvent>();

  /* State */
  readonly panelOpen = signal(false);

  readonly dropdownLabel = computed(() => (this.isMulti()
    ? `${this.value().length || 0} option${this.value().length !== 1 ? 's' : ''} selected`
    : this.options().find((o) => o.value === this.value()[0])?.label));

  readonly menuWidth = computed(
    () => `${this.button().nativeElement.getBoundingClientRect().width}px`,
  );

  protected readonly overlay = viewChild.required(CdkConnectedOverlay);
  protected readonly button = viewChild.required(CdkOverlayOrigin, { read: ElementRef });
  protected readonly menuId = generateId('menu');
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.overlay()
      .backdropClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.panelOpen.set(false));
  }

  toggle() {
    if (this.panelOpen()) {
      this.closePanel();
    } else if (this.canOpen()) {
      this.panelOpen.set(true);
    }
  }

  closePanel() {
    this.panelOpen.set(false);
  }

  protected canOpen(): boolean {
    return !this.panelOpen() && !this.disabled() && this.options().length > 0 && !!this.overlay();
  }

  protected onChange(newValue: TValue[]) {
    this.value.set(newValue);
    if (!this.isMulti()) {
      this.panelOpen.set(false);
    }
  }

  handleBlur(event: FocusEvent) {
    this.blur.emit(event);
  }
}
